import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function RestaurantWheelAlternativePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Restaurant Wheel Alternative — Smarter Than Random Spinners | Hangrily"
        description="Looking for a restaurant wheel spinner alternative? Hangrily uses your preferences, location & real restaurant data instead of random chance. Swipe through filtered options with photos & ratings. Works for couples and groups. Free."
        path="/restaurant-wheel-alternative"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Restaurant Wheel Spinner Alternative - Hangrily',
            description: 'A smarter alternative to random restaurant wheel spinners. Personalized, collaborative, and preference-based.',
            url: 'https://hangrily.app/restaurant-wheel-alternative'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Restaurant Wheel Alternative', item: 'https://hangrily.app/restaurant-wheel-alternative' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Is Hangrily really better than a restaurant wheel?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'If you want a fun, random pick with no stakes — a wheel is fine. But if you actually want to end up at a restaurant everyone enjoys, Hangrily\'s preference-based swiping gets better results every time.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I still get surprised by new restaurants?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Absolutely! Hangrily pulls from real restaurant databases, so you\'ll often discover places you\'ve never heard of. The "Adventure" mood setting specifically prioritizes new and unique spots over familiar chains.'
                }
              },
              {
                '@type': 'Question',
                name: 'How is this different from Yelp or Google Maps?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yelp and Google show you hundreds of options and leave you to decide. Hangrily curates options based on your preferences and walks you through a quick swipe process to narrow down to one winner — especially powerful when deciding with others.'
                }
              },
              {
                '@type': 'Question',
                name: 'Does it work in my city?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily works anywhere that has restaurants listed in major databases. We currently have optimized experiences for 20+ major US cities, but the app works everywhere.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>A Smarter Alternative to Restaurant Wheel Spinners</h1>
          <p className="hero-subtitle">
            Random picks are fun. But you know what's better? A restaurant you actually want to eat at.
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>Why Random Restaurant Pickers Fall Short</h2>
              <p>
                Restaurant wheel spinners and random food pickers are popular because they're simple. 
                Spin a wheel, get a result. But here's the problem: <strong>random doesn't mean good</strong>.
              </p>
              <p>
                A wheel spinner might land on a sushi place when you're craving tacos. It could pick 
                a $$$$-restaurant when you want something cheap. It has no idea that your friend is 
                vegetarian or that your partner hates seafood. And if you're choosing for a group? 
                Forget it — one random pick can't possibly satisfy everyone.
              </p>
              <p>
                <strong>Hangrily takes a different approach.</strong> Instead of random chance, we use 
                your actual preferences to curate restaurant options. Then you swipe through them 
                Tinder-style. The result? A restaurant you genuinely want to eat at — not just wherever 
                a wheel happened to land.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Try the Smarter Way →
              </button>
            </div>
            <div className="column-visual">
              <div className="comparison-box">
                <div className="comparison-side">
                  <h3>🎡 Random Wheel</h3>
                  <ul className="comparison-list negative">
                    <li>No preference filtering</li>
                    <li>Ignores dietary restrictions</li>
                    <li>Can't factor in budget</li>
                    <li>Single person decides</li>
                    <li>No location awareness</li>
                    <li>High disappointment rate</li>
                  </ul>
                </div>
                <div className="comparison-side highlight">
                  <h3>🔥 Hangrily</h3>
                  <ul className="comparison-list positive">
                    <li>Filtered by your preferences</li>
                    <li>Cuisine and mood selection</li>
                    <li>Price range controls ($-$$$$)</li>
                    <li>Everyone swipes and votes</li>
                    <li>GPS-based local restaurants</li>
                    <li>94% satisfaction rate</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Hangrily vs. Random Restaurant Pickers</h2>
          <div className="detailed-comparison">
            <div className="comparison-row">
              <div className="comparison-feature">
                <h3>Personalization</h3>
                <p>
                  <strong>Wheel spinners:</strong> Zero. They pick randomly from a list. <br />
                  <strong>Hangrily:</strong> Filters by cuisine type, price range, distance, mood, and dietary 
                  preferences before you even start swiping.
                </p>
              </div>
            </div>
            <div className="comparison-row">
              <div className="comparison-feature">
                <h3>Group Decisions</h3>
                <p>
                  <strong>Wheel spinners:</strong> One spin for everyone. Hope everyone likes it. <br />
                  <strong>Hangrily:</strong> Every person swipes on restaurants from their own device. The 
                  algorithm finds the restaurant with the most group agreement.
                </p>
              </div>
            </div>
            <div className="comparison-row">
              <div className="comparison-feature">
                <h3>Restaurant Quality</h3>
                <p>
                  <strong>Wheel spinners:</strong> You manually add restaurants to the wheel. Limited to what you already know. <br />
                  <strong>Hangrily:</strong> Pulls real-time data from verified restaurant databases. Discover new 
                  places you didn't know existed with real ratings, photos, and price info.
                </p>
              </div>
            </div>
            <div className="comparison-row">
              <div className="comparison-feature">
                <h3>Satisfaction</h3>
                <p>
                  <strong>Wheel spinners:</strong> ~50% chance of getting something you like (it's literally random). <br />
                  <strong>Hangrily:</strong> 94% of users are happy with their match because every result 
                  is something they actually swiped right on.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2 className="section-title">When to Use Hangrily Instead of a Random Picker</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-icon">💑</span>
              <h3>Couples Can't Agree</h3>
              <p>A wheel spin won't resolve different preferences. Both partners swiping and matching will.</p>
              <Link to="/couples-restaurant-picker" className="use-case-link">Try Couple Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">👥</span>
              <h3>Group Dinner Plans</h3>
              <p>Can't spin a wheel for 8 people. But you can have all 8 people swipe and find consensus.</p>
              <Link to="/group-restaurant-decision" className="use-case-link">Try Group Mode →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">🍜</span>
              <h3>Specific Cravings</h3>
              <p>If you know you want Thai food but can't pick a place, Hangrily curates Thai options for you.</p>
              <Link to="/locations" className="use-case-link">Browse Cuisines →</Link>
            </div>
            <div className="use-case-card">
              <span className="use-case-icon">💰</span>
              <h3>Budget Matters</h3>
              <p>Set your price range and only see restaurants that fit. No surprise $50 entrees.</p>
              <Link to="/features" className="use-case-link">See All Features →</Link>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is Hangrily really better than a restaurant wheel?</h3>
              <p>
                If you want a fun, random pick with no stakes — a wheel is fine. But if you actually want 
                to end up at a restaurant everyone enjoys, Hangrily's preference-based swiping gets 
                better results every time.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can I still get surprised by new restaurants?</h3>
              <p>
                Absolutely! Hangrily pulls from real restaurant databases, so you'll often discover places 
                you've never heard of. The "Adventure" mood setting specifically prioritizes new and unique 
                spots over familiar chains.
              </p>
            </div>
            <div className="faq-item">
              <h3>How is this different from Yelp or Google Maps?</h3>
              <p>
                Yelp and Google show you hundreds of options and leave you to decide. Hangrily curates 
                options based on your preferences and walks you through a quick swipe process to narrow 
                down to one winner — especially powerful when deciding with others.
              </p>
            </div>
            <div className="faq-item">
              <h3>Does it work in my city?</h3>
              <p>
                Hangrily works anywhere that has restaurants listed in major databases. We currently 
                have optimized experiences for 20+ major US cities, but the app works everywhere.
              </p>
            </div>
          </div>
        </section>

        <section className="cta-banner">
          <h2>Upgrade from Random to Smart</h2>
          <p>Ditch the wheel. Try a restaurant picker that actually knows what you like.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Try Hangrily Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
